// 주간 근무시간 — 직원 상세 팝업 (관리 화면)
// 주 이동 · 요일별 기록 · 최근 13주 추세 + 로그인 없이 보는 개인 QR/링크 (자동 생성 · 재발급 · 인쇄)
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Modal, Button, Space, Spin, Typography, Popconfirm, Input, message, Divider } from 'antd';
import {
  LeftOutlined, RightOutlined, QrcodeOutlined, CopyOutlined, PrinterOutlined, ReloadOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import QRCode from 'qrcode';
import { API_BASE } from '../config/api';
import { AuthContext } from '../contexts/AuthContext';
import WorkHoursWeekView from './WorkHoursWeekView';
import { WORK_HOURS_TEXT } from '../i18n/workHoursI18n';

const { Text } = Typography;
const t = WORK_HOURS_TEXT.ko;

const api = axios.create({ baseURL: API_BASE });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const linkUrl = (token) => `${window.location.origin}/h/${token}`;

export const escapeHtml = (s) => String(s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// QR 카드 인쇄 (이름 + QR + URL). 여러 명이면 A4에 격자로 모아 찍고, 부서가 여럿이면 부서마다 새 페이지.
export function printQrCards(cards) {
  const w = window.open('', '_blank', 'width=820,height=900');
  if (!w) { message.warning('팝업이 차단되었습니다. 팝업을 허용해 주세요.'); return; }
  const groups = new Map();
  cards.forEach((c) => {
    const key = c.partName || '';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(c);
  });
  const multi = groups.size > 1;
  const body = [...groups].map(([partName, list]) => `<section class="part">
${multi ? `<div class="part-title">${escapeHtml(partName)} · ${list.length}명</div>` : ''}<div class="grid">${list.map((c) => `<div class="card">
<h1>${escapeHtml(c.name)}</h1><h2>${escapeHtml(c.partName)}</h2>
<img src="${c.qrDataUrl}" alt="QR" /><p class="url">${escapeHtml(c.url)}</p>
</div>`).join('')}</div></section>`).join('');
  w.document.write(`<!doctype html><html lang="ko"><head><meta charset="utf-8"><title>개인 근무시간 QR</title>
<style>
  @page { size: A4; margin: 8mm; }
  body { font-family: -apple-system, 'Malgun Gothic', 'Noto Sans Khmer', sans-serif; margin: 0; }
  .part + .part { break-before: page; page-break-before: always; }
  .part-title { font-size: 11pt; font-weight: 700; margin: 0 0 3mm; padding-bottom: 1mm; border-bottom: 1px solid #333; }
  .grid { display: flex; flex-wrap: wrap; gap: 4mm; justify-content: center; }
  .card { width: 60mm; padding: 4mm; border: 1px dashed #999; border-radius: 3mm; text-align: center; break-inside: avoid; page-break-inside: avoid; }
  h1 { font-size: 14pt; margin: 0 0 1mm; } h2 { font-size: 9pt; margin: 0 0 2mm; color: #555; font-weight: 400; }
  img { width: 40mm; height: 40mm; }
  .url { font-family: Menlo, Consolas, monospace; font-size: 6.5pt; margin: 1mm 0 0; word-break: break-all; color: #333; }
</style></head><body>${body}
<script>window.onload = function () { window.print(); };</script></body></html>`);
  w.document.close();
}

const EmployeeWorkHoursModal = ({ employeeNo, open, onClose, initialDate }) => {
  const { auth } = useContext(AuthContext);
  const [date, setDate] = useState(initialDate);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [qr, setQr] = useState(null);

  // 재발급 버튼은 야근관리 '수정' 또는 권한관리 권한일 때만 (서버도 같은 기준으로 막는다)
  const perms = auth?.menuPermissions || [];
  const yes = (v) => v === 1 || v === '1' || v === true;
  const canManageLink = perms.some((p) => (p.menu_key === 'overtime' && yes(p.can_update)) || (p.menu_key === 'auth-admin' && yes(p.can_view)));

  useEffect(() => { if (open) setDate(initialDate); }, [open, initialDate]);

  const fetchDetail = useCallback(async () => {
    if (!employeeNo || !date) return;
    setLoading(true);
    try {
      const { data: res } = await api.get(`/api/work-hours/employee/${employeeNo}`, { params: { date } });
      setData(res && res.success ? res : null);
    } catch (e) {
      setData(null);
      message.error(e.response?.data?.message || '직원 근무시간을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [employeeNo, date]);

  useEffect(() => { if (open) fetchDetail(); }, [open, fetchDetail]);

  const token = data?.link?.token;
  useEffect(() => {
    if (!token) { setQr(null); return; }
    QRCode.toDataURL(linkUrl(token), { width: 360, margin: 1 }).then(setQr).catch(() => setQr(null));
  }, [token]);

  const reissueLink = async () => {
    setLinkBusy(true);
    try {
      const { data: res } = await api.post(`/api/work-hours/employee/${employeeNo}/link`);
      setData((d) => ({ ...d, link: res.link }));
      message.success('QR을 새로 만들었습니다. 기존 QR은 더 이상 열리지 않습니다.');
    } catch (e) {
      message.error(e.response?.data?.message || '재발급에 실패했습니다.');
    } finally {
      setLinkBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(linkUrl(token));
      message.success('링크를 복사했습니다.');
    } catch (_) {
      message.info(linkUrl(token));
    }
  };

  const emp = data?.employee;
  const week = data?.week;
  const isThisWeek = week && dayjs().isBefore(dayjs(week.week_end).add(1, 'day')) && !dayjs().isBefore(dayjs(week.week_start));

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={640}
      destroyOnClose
      className="whv-modal"
      title={emp ? (
        <Space size={8} wrap>
          <span>{emp.name}</span>
          <Text type="secondary" style={{ fontWeight: 400 }}>{emp.part_name}{emp.rank ? ` · ${emp.rank}` : ''}</Text>
        </Space>
      ) : '근무시간 상세'}
    >
      <Spin spinning={loading}>
        {week && (
          <div className="whv-weeknav">
            <Button type="text" icon={<LeftOutlined />} onClick={() => setDate(dayjs(week.week_start).subtract(7, 'day').format('YYYY-MM-DD'))} aria-label="이전 주" />
            <span className="whv-weeknav-label">
              {dayjs(week.week_start).format('YYYY.M.D')}(월) ~ {dayjs(week.week_end).format('M.D')}(일)
            </span>
            <Button type="text" icon={<RightOutlined />} onClick={() => setDate(dayjs(week.week_start).add(7, 'day').format('YYYY-MM-DD'))} aria-label="다음 주" />
            {!isThisWeek && <Button size="small" type="link" onClick={() => setDate(dayjs().format('YYYY-MM-DD'))}>{t.thisWeek}</Button>}
          </div>
        )}

        <WorkHoursWeekView
          week={week}
          trend={data?.trend}
          limit={data?.limit_hours}
          t={t}
          onSelectWeek={(ws) => setDate(ws)}
        />

        {emp && (
          <>
            <Divider style={{ margin: '16px 0 12px' }} />
            <div className="whv-link">
              <div className="whv-link-head">
                <QrcodeOutlined />
                <b>개인 QR</b>
              </div>
              {token && (
                <div className="whv-link-body">
                  {qr && <img src={qr} alt="개인 QR" className="whv-qr" />}
                  <div className="whv-link-info">
                    <Input readOnly value={linkUrl(token)} size="small" addonAfter={<CopyOutlined onClick={copyLink} />} />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      열람 {data.link.access_count}회{data.link.last_accessed_at ? ` · 최근 ${data.link.last_accessed_at}` : ''}
                    </Text>
                    <Space wrap size={6}>
                      <Button size="small" icon={<PrinterOutlined />} disabled={!qr} onClick={() => printQrCards([{ name: emp.name, partName: emp.part_name, url: linkUrl(token), qrDataUrl: qr }])}>QR 카드 인쇄</Button>
                      {canManageLink && (
                        <Popconfirm title="QR을 새로 만들까요?" description="QR을 잃어버렸거나 남에게 알려졌을 때 사용합니다. 기존 QR은 즉시 열리지 않습니다." okText="재발급" cancelText="취소" onConfirm={reissueLink}>
                          <Button size="small" icon={<ReloadOutlined />} loading={linkBusy}>재발급</Button>
                        </Popconfirm>
                      )}
                    </Space>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </Spin>
    </Modal>
  );
};

export default EmployeeWorkHoursModal;
